import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, BRAND } from "../api.js";
import { Icon } from "../components/Icon.js";
import {
  FEEDBACK_STATUSES,
  FEEDBACK_STATUS_FILTERS,
  FEEDBACK_TYPE_FILTERS,
  feedbackListApiPath,
  feedbackItemStatusLabel,
  feedbackStatusApiPath,
  feedbackStatusLabel,
  feedbackTypeLabel,
  resolveFeedbackStatusFilter,
  resolveFeedbackTypeFilter,
  type FeedbackStatus,
  type FeedbackStatusFilter,
  type FeedbackType,
  type FeedbackTypeFilter,
} from "./feedback-admin.js";

interface FeedbackItem {
  id: string;
  typ: FeedbackType;
  rubrik: string;
  text: string;
  status: FeedbackStatus;
  plattform: string | null;
  appVersion: string | null;
  createdAt: string;
  reporter: { id: string; email: string | null; displayName: string | null } | null;
}

interface FeedbackCounts {
  alla: number;
  olost: number;
  oppen: number;
  pagar: number;
  stangd: number;
  bug: number;
  onskemal: number;
}

interface FeedbackResponse {
  feedback: FeedbackItem[];
  total: number;
  counts: FeedbackCounts;
}

const emptyCounts: FeedbackCounts = {
  alla: 0,
  olost: 0,
  oppen: 0,
  pagar: 0,
  stangd: 0,
  bug: 0,
  onskemal: 0,
};

function reporterLabel(item: FeedbackItem): string {
  if (!item.reporter) return "Raderat eller anonymiserat konto";
  return (
    item.reporter.displayName || item.reporter.email || `Användare ${item.reporter.id.slice(0, 8)}`
  );
}

export function FeedbackPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = resolveFeedbackStatusFilter(searchParams.get("status"));
  const typ = resolveFeedbackTypeFilter(searchParams.get("typ"));
  const [data, setData] = useState<FeedbackResponse>({
    feedback: [],
    total: 0,
    counts: emptyCounts,
  });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api<FeedbackResponse>(feedbackListApiPath(status, typ)));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Feedback kunde inte hämtas.");
    } finally {
      setLoading(false);
    }
  }, [status, typ]);

  useEffect(() => {
    void load();
  }, [load]);

  const setFilters = (nextStatus: FeedbackStatusFilter, nextType: FeedbackTypeFilter) => {
    setSearchParams({ status: nextStatus, typ: nextType }, { replace: true });
  };

  const changeStatus = async (item: FeedbackItem, nextStatus: FeedbackStatus) => {
    if (item.status === nextStatus || busyId) return;
    setBusyId(item.id);
    setError(null);
    setNotice(null);
    try {
      await api(feedbackStatusApiPath(item.id), {
        method: "PATCH",
        body: { status: nextStatus },
      });
      setNotice(
        `”${item.rubrik}” markerades som ${feedbackItemStatusLabel(nextStatus).toLowerCase()}.`,
      );
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Statusen kunde inte sparas.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="page-stack">
      <header className="page-header">
        <div>
          <div className="eyebrow">{BRAND.name} Admin</div>
          <h1>Felanmälningar &amp; önskemål</h1>
          <p>
            Läs användarens faktiska rapport och följ den från öppen till pågående eller stängd.
            Varje statusändring auditloggas.
          </p>
        </div>
      </header>

      {error && <div className="notice danger">{error}</div>}
      {notice && <div className="notice success">{notice}</div>}

      <section className="panel feedback-filters" aria-label="Filtrera feedback">
        <div>
          <strong>Status</strong>
          <div className="filter-tabs">
            {FEEDBACK_STATUS_FILTERS.map((filter) => (
              <button
                type="button"
                className={filter === status ? "active" : ""}
                key={filter}
                onClick={() => setFilters(filter, typ)}
              >
                {feedbackStatusLabel(filter)}
                <span>{data.counts[filter]}</span>
              </button>
            ))}
          </div>
        </div>
        <div>
          <strong>Typ</strong>
          <div className="filter-tabs">
            {FEEDBACK_TYPE_FILTERS.map((filter) => (
              <button
                type="button"
                className={filter === typ ? "active" : ""}
                key={filter}
                onClick={() => setFilters(status, filter)}
              >
                {feedbackTypeLabel(filter)}
                <span>{filter === "alla" ? data.counts.alla : data.counts[filter]}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {loading ? (
        <div className="panel loading-panel" role="status">
          Hämtar felanmälningar …
        </div>
      ) : data.feedback.length === 0 ? (
        <div className="panel empty-state">
          <span className="empty-icon">
            <Icon name="check" />
          </span>
          <h2>Inga poster i det här urvalet</h2>
          <p>Det finns inget som kräver hantering med de valda filtren.</p>
        </div>
      ) : (
        <section className="feedback-list" aria-label={`${data.total} feedbackposter`}>
          {data.feedback.map((item) => (
            <article className={`panel feedback-card ${item.typ}`} key={item.id}>
              <header className="feedback-card-header">
                <div>
                  <div className="feedback-badges">
                    <span className={`badge ${item.typ === "bug" ? "err" : "warn"}`}>
                      {feedbackTypeLabel(item.typ)}
                    </span>
                    <span className={`status-chip feedback-${item.status}`}>
                      {feedbackItemStatusLabel(item.status)}
                    </span>
                  </div>
                  <h2>{item.rubrik}</h2>
                </div>
                <time dateTime={item.createdAt}>
                  {new Date(item.createdAt).toLocaleString("sv-SE", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </time>
              </header>

              <p className="feedback-body">{item.text}</p>

              <dl className="feedback-meta">
                <div>
                  <dt>Rapportör</dt>
                  <dd>{reporterLabel(item)}</dd>
                </div>
                <div>
                  <dt>Plattform</dt>
                  <dd>{item.plattform ?? "Ej angiven"}</dd>
                </div>
                <div>
                  <dt>Appversion</dt>
                  <dd>{item.appVersion ?? "Ej angiven"}</dd>
                </div>
                <div>
                  <dt>Ticket-ID</dt>
                  <dd className="feedback-id">{item.id}</dd>
                </div>
              </dl>

              <footer className="feedback-actions" aria-label={`Ändra status för ${item.rubrik}`}>
                <span>Status:</span>
                {FEEDBACK_STATUSES.map((nextStatus) => (
                  <button
                    type="button"
                    className={nextStatus === item.status ? "btn" : "btn feedback-secondary"}
                    key={nextStatus}
                    disabled={busyId === item.id || nextStatus === item.status}
                    aria-pressed={nextStatus === item.status}
                    onClick={() => void changeStatus(item, nextStatus)}
                  >
                    {busyId === item.id && nextStatus !== item.status
                      ? "Sparar …"
                      : feedbackItemStatusLabel(nextStatus)}
                  </button>
                ))}
              </footer>
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
